import React, { useEffect } from 'react'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import { constants, findPermission, getStorage } from 'helpers'
import {
    bulkEnrollmentExitStepFromPathname,
    setBulkEnrollmentCurrentStep
} from 'helpers/bulkEnrollmentAnalytics'
import { useBulkEnrollmentAnalytics } from 'hooks/useBulkEnrollmentAnalytics'

const isBulkEnrollmentPath = (pathname: string) =>
    pathname.includes('/bulk-enrollment')

export const BulkEnrollmentLayout = () => {
    const { villageId } = useParams()
    const { pathname } = useLocation()
    const { trackStarted, trackAbandoned } = useBulkEnrollmentAnalytics(
        villageId
    )
    const canViewBulkEnrollment = findPermission(
        getStorage(constants.USER_PERMISSIONS),
        constants.CAN_VIEW_BULK_ENROLLMENT
    )

    useEffect(() => {
        if (!canViewBulkEnrollment) {
            return
        }
        // Entry owns the step via bulkEnrollmentExitStepFromRow. Parent
        // effects run after the child's and would otherwise overwrite
        // name_entry / row_confirm with phone_entry.
        if (pathname.includes('/bulk-enrollment/entry')) {
            return
        }
        setBulkEnrollmentCurrentStep(
            bulkEnrollmentExitStepFromPathname(pathname)
        )
    }, [pathname, canViewBulkEnrollment])

    useEffect(() => {
        if (!canViewBulkEnrollment) {
            return
        }
        trackStarted()

        // pagehide with persisted=false is a real unload / tab close.
        // persisted=true is bfcache (user may return). visibilitychange
        // hidden is backgrounding — do not close the session.
        const handlePageHide = (event: PageTransitionEvent) => {
            if (event.persisted) {
                return
            }
            trackAbandoned()
        }

        window.addEventListener('pagehide', handlePageHide)
        return () => {
            window.removeEventListener('pagehide', handlePageHide)
            // On SPA leave, history has already updated. StrictMode remount
            // and in-flow navigation still have /bulk-enrollment in the URL.
            if (!isBulkEnrollmentPath(window.location.pathname)) {
                trackAbandoned()
            }
        }
    }, [canViewBulkEnrollment])

    if (!canViewBulkEnrollment) {
        return (
            <Navigate
                to={villageId ? `/village/${villageId}` : '/village'}
                replace
            />
        )
    }

    return <Outlet />
}
